import { useState, type CSSProperties, type FormEvent } from "react";
import { C, fieldStyle, font } from "../theme";
import { validatePriceRule, type PriceRuleFieldErrors } from "../lib/pricingValidation";
import type { CourtDto, CreatePriceRuleRequest, PriceRuleDto, WeekDay } from "../types";
import { Button } from "./Button";
import { Card } from "./Card";
import { FieldInput } from "./Field";
import { Label } from "./Label";
import { TimePicker } from "./DateTimePicker";

const DAYS: Array<{ value: WeekDay; label: string }> = [
  { value: 0, label: "Domingo" },
  { value: 1, label: "Segunda-feira" },
  { value: 2, label: "Terça-feira" },
  { value: 3, label: "Quarta-feira" },
  { value: 4, label: "Quinta-feira" },
  { value: 5, label: "Sexta-feira" },
  { value: 6, label: "Sábado" },
];

interface PriceRuleFormProps {
  courts: CourtDto[];
  initialRule?: PriceRuleDto | null;
  submitting: boolean;
  submitError: string | null;
  onSubmit: (payload: CreatePriceRuleRequest) => void;
  onCancel: () => void;
}

function timeValue(value?: string): string {
  return value?.slice(0, 5) ?? "";
}

function apiTime(value: string): string {
  return value.length === 5 ? `${value}:00` : value;
}

export function PriceRuleForm({ courts, initialRule, submitting, submitError, onSubmit, onCancel }: PriceRuleFormProps) {
  const [courtId, setCourtId] = useState(initialRule?.courtId ?? "");
  const [name, setName] = useState(initialRule?.name ?? "");
  const [startTime, setStartTime] = useState(timeValue(initialRule?.startTime));
  const [endTime, setEndTime] = useState(timeValue(initialRule?.endTime));
  const [hourlyRate, setHourlyRate] = useState(initialRule ? String(initialRule.hourlyRate) : "");
  const [priority, setPriority] = useState(initialRule ? String(initialRule.priority) : "0");
  const [isActive, setIsActive] = useState(initialRule?.isActive ?? true);
  const [days, setDays] = useState<WeekDay[]>(initialRule?.days ?? []);
  const [errors, setErrors] = useState<PriceRuleFieldErrors>({});

  const toggleDay = (day: WeekDay) => {
    setDays(current => current.includes(day) ? current.filter(value => value !== day) : [...current, day].sort());
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    const payload: CreatePriceRuleRequest = {
      courtId: courtId || null,
      name: name.trim(),
      startTime: apiTime(startTime),
      endTime: apiTime(endTime),
      hourlyRate: Number(hourlyRate),
      priority: Number(priority),
      isActive,
      days,
    };
    const nextErrors = validatePriceRule(payload);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    onSubmit(payload);
  };

  const labelStyle: CSSProperties = { display: "block", fontFamily: font, fontSize: 12, fontWeight: 600, color: C.gray, marginBottom: 4, marginTop: 12 };
  const errorStyle: CSSProperties = { color: C.red, fontSize: 11.5, marginTop: 4 };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="price-rule-title" style={{ position: "fixed", inset: 0, background: C.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
      <Card style={{ width: "100%", maxWidth: 640, maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
          <div id="price-rule-title"><Label>{initialRule ? "Editar regra de preço" : "Nova regra de preço"}</Label></div>
          <Button variant="ghost" onClick={onCancel} disabled={submitting} ariaLabel="Fechar">✕</Button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <label htmlFor="price-rule-name" style={labelStyle}>Nome da regra</label>
          <FieldInput id="price-rule-name" value={name} maxLength={121} disabled={submitting} onChange={event => setName(event.target.value)} placeholder="Ex.: Horário nobre noturno" />
          {errors.name && <div style={errorStyle}>{errors.name}</div>}

          <label htmlFor="price-rule-court" style={labelStyle}>Quadra da regra</label>
          <select id="price-rule-court" value={courtId} disabled={submitting} onChange={event => setCourtId(event.target.value)} style={{ ...fieldStyle, width: "100%", padding: 10 }}>
            <option value="">Todas as quadras (regra global)</option>
            {courts.map(court => <option key={court.id} value={court.id}>{court.name}</option>)}
          </select>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
            <div>
              <label htmlFor="price-rule-start" style={labelStyle}>Início da regra</label>
              <TimePicker id="price-rule-start" value={startTime} disabled={submitting} ariaLabel="Início da regra" onChange={setStartTime} />
            </div>
            <div>
              <label htmlFor="price-rule-end" style={labelStyle}>Fim da regra</label>
              <TimePicker id="price-rule-end" value={endTime} disabled={submitting} ariaLabel="Fim da regra" onChange={setEndTime} />
            </div>
            <div>
              <label htmlFor="price-rule-rate" style={labelStyle}>Valor por hora</label>
              <FieldInput id="price-rule-rate" type="number" min="0" step="0.01" value={hourlyRate} disabled={submitting} onChange={event => setHourlyRate(event.target.value)} />
            </div>
            <div>
              <label htmlFor="price-rule-priority" style={labelStyle}>Prioridade</label>
              <FieldInput id="price-rule-priority" type="number" min="0" step="1" value={priority} disabled={submitting} onChange={event => setPriority(event.target.value)} />
            </div>
          </div>
          {errors.interval && <div style={errorStyle}>{errors.interval}</div>}
          {errors.hourlyRate && <div style={errorStyle}>{errors.hourlyRate}</div>}
          {errors.priority && <div style={errorStyle}>{errors.priority}</div>}

          <fieldset style={{ border: 0, padding: 0, margin: "16px 0 0" }}>
            <legend style={{ ...labelStyle, marginTop: 0 }}>Dias aplicáveis</legend>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {DAYS.map(day => (
                <label key={day.value} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: C.gray, border: `1px solid ${days.includes(day.value) ? C.ink : C.faint}`, borderRadius: 16, padding: "6px 10px", cursor: "pointer" }}>
                  <input type="checkbox" checked={days.includes(day.value)} disabled={submitting} onChange={() => toggleDay(day.value)} />
                  {day.label}
                </label>
              ))}
            </div>
            {errors.days && <div style={errorStyle}>{errors.days}</div>}
          </fieldset>

          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5, color: C.gray, marginTop: 16 }}>
            <input type="checkbox" checked={isActive} disabled={submitting} onChange={event => setIsActive(event.target.checked)} />
            Regra ativa
          </label>

          {submitError && <div role="alert" style={{ ...errorStyle, background: C.bg, borderRadius: 8, padding: 10, marginTop: 14 }}>{submitError}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button>
            <Button type="submit" disabled={submitting}>{submitting ? "Salvando..." : initialRule ? "Salvar regra" : "Criar regra"}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
